import React, { useState, useEffect } from 'react';

// --- Types ---
interface TimeElapsedProps {
  targetDate: Date;
}

interface TimeState {
  years: number;
  months: number;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

// --- Sub-Component for UI ---
const TimeUnit: React.FC<{ value: number; label: string }> = ({ value, label }) => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1.5rem 1rem',
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    border: '1px solid #f3f4f6',
    minWidth: '65px'
  }}>
    <span style={{
      fontSize: '1.875rem',
      fontWeight: 300,
      color: '#1f2937',
      fontVariantNumeric: 'tabular-nums'
    }}>
      {value.toString().padStart(2, '0')}
    </span>
    <span style={{
      fontSize: '0.65rem',
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      color: '#9ca3af',
      fontWeight: 600,
      marginTop: '4px'
    }}>
      {label}
    </span>
  </div>
);

// --- Main Component ---
const TimeElapsedCounter: React.FC<TimeElapsedProps> = ({ targetDate } : { targetDate: Date }) => {
  const [elapsed, setElapsed] = useState<TimeState>({
    years: 0, months: 0, days: 0, hours: 0, minutes: 0, seconds: 0
  });

  useEffect(() => {
    const calculate = () => {
      const start = targetDate;
      const now = new Date();
      if (start > now) {
        setElapsed({ years: 0, months: 0, days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      let years = now.getFullYear() - start.getFullYear();
      let months = now.getMonth() - start.getMonth();
      let days = now.getDate() - start.getDate();
      let hours = now.getHours() - start.getHours();
      let minutes = now.getMinutes() - start.getMinutes();
      let seconds = now.getSeconds() - start.getSeconds();

      if (seconds < 0) { seconds += 60; minutes--; }
      if (minutes < 0) { minutes += 60; hours--; }
      if (hours < 0) { hours += 24; days--; }
      if (days < 0) {
        const prevMonth = new Date(now.getFullYear(), now.getMonth(), 0).getDate();
        days += prevMonth;
        months--;
      }
      if (months < 0) { months += 12; years--; }

      setElapsed({ years, months, days, hours, minutes, seconds });
    };

    calculate();
    const timer = setInterval(calculate, 1000);
    return () => clearInterval(timer);
  }, [targetDate]);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '2rem',
      padding: '2.5rem',
      backgroundColor: '#fafafa',
      borderRadius: '24px',
      maxWidth: '700px',
      margin: '2rem auto',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <div style={{ textAlign: 'center' }}>
        <h2 style={{
          fontSize: '0.75rem',
          fontWeight: 500,
          color: '#6b7280',
          textTransform: 'uppercase',
          letterSpacing: '0.2em',
          marginBottom: '0.25rem'
        }}>
          Magyar Péter ennyi ideje nem küldött a frontra senkit
        </h2>
        <p style={{ fontSize: '0.75rem', color: '#9ca3af', fontStyle: 'italic' }}>
          {targetDate.toLocaleDateString('hu')} - {new Date().toLocaleDateString('hu')}
        </p>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))',
        gap: '1rem',
        width: '100%'
      }}>
        <TimeUnit value={elapsed.years} label="Év" />
        <TimeUnit value={elapsed.months} label="Hónap" />
        <TimeUnit value={elapsed.days} label="Nap" />
        <TimeUnit value={elapsed.hours} label="Óra" />
        <TimeUnit value={elapsed.minutes} label="Perc" />
        <TimeUnit value={elapsed.seconds} label="Másodperc" />
      </div>
    </div>
  );
};

export default TimeElapsedCounter;
